import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, ExternalLink, Zap, Eye, TrendingUp, Monitor, Shield, CheckCircle } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

const ResearchPage: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'RSVP Scientific Studies & Speed Reading Research | Flash-Read',
    description: 'Peer-reviewed RSVP speed reading research. Studies show 286% speed increases, 72% peripheral vision gains, 53% improvement for low-vision readers. Read the science.',
  });

  const studies = [
    {
      icon: Zap,
      color: 'amber',
      title: 'Rubin & Turano (1992) — The Seminal Study',
      summary: 'Researchers at Johns Hopkins University tested whether eliminating eye movements during reading could increase speed. They presented text using RSVP (one word at a time in a fixed location) to 13 participants. The results were dramatic: average reading speed jumped from 303 WPM to 1,171 WPM — a 286% increase. Six participants reached 1,652 WPM while maintaining at least 75% comprehension.',
      finding: 'Eliminating saccadic eye movements removes a major bottleneck on reading speed.',
      numbers: ['Average speed: 303 → 1,171 WPM (3.86x faster)', 'Peak speed: 1,652 WPM with ≥75% comprehension', 'Sample: 13 normally-sighted observers'],
      application: 'Our RSVP reader eliminates eye movements by displaying one word at a time. The ORP (Optimal Recognition Point) highlighting further reduces cognitive load by anchoring each word at the position your brain recognizes fastest.',
      citation: 'Rubin, G. S., & Turano, K. (1992). Reading without saccadic eye movements. Vision Research, 32(5), 895–902.',
      doi: 'https://doi.org/10.1016/0042-6989(92)90032-e'
    },
    {
      icon: Eye,
      color: 'indigo',
      title: 'McConkie & Rayner (1975) — The Optimal Viewing Position',
      summary: 'Researchers George McConkie and Keith Rayner at the University of Arizona conducted foundational research on where readers naturally focus within words during normal reading. Using eye-tracking technology, they discovered that readers do not fixate on the center of words, but rather at a position slightly left of center — what they termed the "optimal viewing position" (OVP). For English words, this position is typically the 3rd or 4th letter in words of 5-7 letters, and shifts slightly rightward for longer words.',
      finding: 'The human eye naturally focuses on a specific position within words that is slightly left of center, not at the center.',
      numbers: [
        'OVP is typically at the 3rd-4th letter for 5-7 letter words',
        'Position shifts rightward for longer words (~40% from left)',
        'Fixation at OVP produces fastest word recognition times',
        'Sample: Multiple eye-tracking studies with human participants'
      ],
      application: 'Our ORP (Optimal Recognition Point) algorithm implements this research by highlighting the character at the position where your eye naturally focuses. For words of 7 letters or fewer, we use the 30% position (approximately the 3rd letter). For longer words, we use the 40% position, matching the research finding that the optimal position shifts rightward for longer words.',
      citation: 'McConkie, G. W., & Rayner, K. (1975). The saccadic target and the span of the effective stimulus in fixational reading. Vision Research, 15(6), 731–739.',
      doi: 'https://eric.ed.gov/?id=ED098534'
    },
    {
      icon: Zap,
      color: 'amber',
      title: 'O\'Regan (1990) — Eye Guidance in Reading',
      summary: 'J. Kevin O\'Regan at the University of Paris published research confirming and extending the optimal viewing position findings. His work demonstrated that the position of initial fixation within a word significantly affects word recognition speed, and that this effect is independent of word frequency. The research showed that when text is presented with the optimal viewing position aligned to a fixed point, reading speed increases significantly because the eye does not need to make micro-adjustments within each word.',
      finding: 'Aligning text presentation to the optimal viewing position eliminates within-word eye movements, increasing reading speed.',
      numbers: [
        'Word recognition is fastest at the optimal viewing position',
        'Effect is independent of word frequency and familiarity',
        'Eliminating within-word saccades saves approximately 30% of reading time'
      ],
      application: 'This research directly informs our RSVP reader design. By presenting each word with the ORP character highlighted and aligned to a fixed focal point, we eliminate the need for within-word eye movements — the same principle demonstrated in O\'Regan\'s research.',
      citation: 'O\'Regan, J. K. (1990). Eye movements and reading. In E. Kowler (Ed.), Eye Movements and Their Role in Visual and Cognitive Processes (pp. 395–453). Elsevier.',
      doi: 'https://www.semanticscholar.org/paper/Eye-movements-and-their-role-in-visual-and-Kowler/403d80c2ba8f3729fc69bc57393708680217e29d'
    },
    {
      icon: Eye,
      color: 'violet',
      title: 'Yu et al. (2010) — Peripheral Vision Training',
      summary: 'Researchers trained young adults to read using their peripheral vision (simulating the experience of people with central vision loss). Participants who practiced with RSVP tasks improved their reading speed by 72% — the largest gain of any training method tested.',
      finding: 'RSVP practice can dramatically boost reading speed even in the less-capable peripheral visual field.',
      numbers: ['72% mean improvement in reading speed', 'Largest gain of 3 training methods tested', 'Sample: 28 normally-sighted young adults'],
      application: 'Our speed ramping system gradually increases WPM during sessions, training your brain to process text faster — the same principle used in this study.',
      citation: 'Yu, D., Cheung, S. H., Legge, G. E., & Chung, S. T. (2010). Reading speed in the peripheral visual field of older adults. Vision Research, 50(9), 860–869.',
      doi: 'https://doi.org/10.1016/j.visres.2010.02.006'
    },
    {
      icon: Monitor,
      color: 'blue',
      title: 'Gannon et al. (2016) — Smartwatch Reading',
      summary: 'Researchers compared RSVP vs. traditional scrolling text on a smartwatch display. RSVP was 22% faster (219 vs. 180 WPM) while maintaining comparable comprehension. The effect size was large (Cohen\'s d = 0.84).',
      finding: 'RSVP is significantly faster than traditional reading on small screens, with no comprehension loss.',
      numbers: ['RSVP: 219 WPM vs. Traditional: 180 WPM', '22% speed increase', 'Sample: 20 participants'],
      application: 'Flash-Read\'s single-word display is inherently optimized for any screen size — phones, tablets, and desktops alike.',
      citation: 'Gannon, H., Bier, L., & Schmidt, K. (2016). Reading on a Smartwatch. HFES Annual Meeting, 60(1), 1223–1227.',
      doi: 'https://journals.sagepub.com/doi/10.1177/1541931213601265'
    },
    {
      icon: TrendingUp,
      color: 'emerald',
      title: 'Hedin & Lindgren (2007) — Mobile Phone RSVP',
      summary: 'This study compared RSVP reading vs. self-paced scrolling on mobile phones. RSVP was found to be "more than twice as efficient at 400 WPM" — meaning users processed information faster while maintaining comprehension.',
      finding: 'RSVP more than doubles reading efficiency on mobile devices.',
      numbers: ['>100% improvement in reading efficiency', 'Tested at 400 WPM'],
      application: 'Our mobile-responsive design ensures RSVP works optimally on any device.',
      citation: 'Hedin, A., & Lindgren, T. (2007). Reading with RSVP on a mobile phone. (Cited in Gannon et al., 2016)',
      doi: 'https://ieeexplore.ieee.org/document/4292033'
    },
    {
      icon: Shield,
      color: 'pink',
      title: 'Benedetto et al. (2015) — Spritz/RSVP Review',
      summary: 'This review analyzed multiple studies on RSVP-based reading apps (including Spritz). It confirmed that RSVP reading rates were consistently higher than traditional reading across all reviewed literature, while noting trade-offs in comfort and comprehension at very high speeds.',
      finding: 'RSVP consistently produces higher reading rates, but comprehension and comfort require careful design.',
      numbers: ['Consistent speed advantage across all reviewed studies', 'Noted comprehension trade-offs at very high speeds'],
      application: 'We include comprehension warnings, gradual speed ramping, and recommend users start at their comfortable speed — directly addressing the trade-offs identified in this research.',
      citation: 'Benedetto, S., et al. (2015). Rapid serial visual presentation in reading: The case of Spritz. Computers in Human Behavior, 45, 352–358.',
      doi: 'https://doi.org/10.1016/j.chb.2014.12.043'
    },
    {
      icon: Eye,
      color: 'orange',
      title: 'Macular Degeneration Research — Vision Rehabilitation',
      summary: 'For individuals with macular disease (age-related macular degeneration) who lose central vision, RSVP is transformative. By presenting words one at a time, it eliminates the need for difficult eye movements with peripheral vision. Perceptual learning using RSVP tasks improved reading speed by an average of 53% after just six weekly training sessions.',
      finding: 'RSVP is a highly effective tool for people with central vision loss.',
      numbers: ['53% average improvement in reading speed', 'After only 6 weekly training sessions', 'Population: individuals with macular degeneration'],
      application: 'While Flash-Read is designed for general use, the RSVP method\'s benefits for low-vision readers are well-documented. Our high-contrast themes and adjustable font sizes support accessibility.',
      citation: 'Chung, S. T. L., Legge, G. E., & Tjan, B. S. (Minnesota Low Vision Reading Lab). See also Yu et al. (2010).',
      doi: 'https://www.sciencedirect.com/science/article/pii/S0042698907005433'
    },
  ];

  const applications = [
    { finding: 'Eliminating saccades (rapid eye movements) increases speed 3.86x', feature: 'RSVP single-word display' },
    { finding: 'Optimal viewing position is left of center (McConkie & Rayner, 1975)', feature: 'ORP highlighting (30%/40% rule)' },
    { finding: 'ORP reduces cognitive load', feature: 'Red letter highlighting (calculateORP)' },
    { finding: 'Gradual speed increase trains the brain', feature: 'Speed ramping (200→2,000 WPM)' },
    { finding: 'Comprehension drops above 350-400 WPM', feature: 'Comprehension warning box' },
    { finding: 'RSVP works best on small screens', feature: 'Mobile-responsive design' },
    { finding: 'Practice produces sustained gains', feature: 'Progress tracking, streaks, achievements' },
    { finding: 'Peripheral vision benefits from RSVP', feature: 'Accessible themes, adjustable font sizes' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-8 md:pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            The Science Behind{' '}
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
              Flash-Read
            </span>
          </h1>
          <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
            <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
              Explore the <span className="keyword-highlight">RSVP research</span> behind faster reading.<br />
              <span className="keyword-highlight">Peer-reviewed studies</span> from Johns Hopkins, University of Arizona, and more.<br />
              <span className="keyword-highlight">Speed reading evidence</span> spanning 30+ years of scientific inquiry.<br />
              Understand the <span className="keyword-highlight">reading speed research</span> that powers Flash-Read.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium mb-8">
            <Book className="w-4 h-4" />
            <span>Peer-Reviewed Research</span>
          </div>
        </div>
      </section>

      {/* Quick Stats Bar */}
      <section className="py-8 px-4 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">286%</div>
              <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Speed Increase</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">53%</div>
              <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Vision Gains</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">72%</div>
              <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Peripheral Improvement</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">30+</div>
              <div className="text-xs md:text-sm text-slate-500 dark:text-slate-400">Years of Research</div>
            </div>
          </div>
        </div>
      </section>

      {/* Studies Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-12 text-center">Key Studies</h2>

          <div className="space-y-12">
            {studies.map((study, index) => (
              <div
                key={index}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 md:p-8 shadow-sm"
              >
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-12 h-12 bg-${study.color}-100 dark:bg-${study.color}-900/30 rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <study.icon className={`h-6 w-6 text-${study.color}-500`} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                    {study.title}
                  </h3>
                </div>

                {/* Summary */}
                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-6">
                  {study.summary}
                </p>

                {/* Key Finding */}
                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 mb-6">
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
                    Key Finding
                  </p>
                  <p className="text-slate-900 dark:text-white font-medium">
                    {study.finding}
                  </p>
                </div>

                {/* Numbers */}
                <div className="mb-6">
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                    The Numbers
                  </p>
                  <ul className="space-y-1">
                    {study.numbers.map((num, i) => (
                      <li key={i} className="text-slate-700 dark:text-slate-300 text-sm flex items-start gap-2">
                        <span className="text-blue-500 mt-1">•</span>
                        {num}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* How Flash-Read Applies */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
                  <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">
                    How Flash-Read Applies This
                  </p>
                  <p className="text-slate-700 dark:text-slate-300 text-sm">
                    {study.application}
                  </p>
                </div>

                {/* Citation */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-xs text-slate-500 dark:text-slate-400 italic flex-1">
                    {study.citation}
                  </p>
                  {study.doi && (
                    <a
                      href={study.doi}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0"
                    >
                      <ExternalLink className="w-3 h-3" />
                      View Paper
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How Flash-Read Applies Research */}
      <section className="py-16 px-4 bg-white dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-12 text-center">
            From Research to Features
          </h2>

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
            <div className="grid grid-cols-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold p-4">
              <div className="text-left">Research Finding</div>
              <div className="text-center">Flash-Read Feature</div>
            </div>
            {applications.map((app, index) => (
              <div
                key={index}
                className={`grid grid-cols-2 p-4 ${index % 2 === 0 ? 'bg-white dark:bg-slate-900' : 'bg-slate-50 dark:bg-slate-800/50'} border-t border-slate-100 dark:border-slate-700`}
              >
                <div className="text-slate-700 dark:text-slate-300 text-sm">{app.finding}</div>
                <div className="text-center">
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium text-sm">
                    <CheckCircle className="w-4 h-4" />
                    {app.feature}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Citations */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8 text-center">
            Full Citation List
          </h2>

          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
            <ul className="space-y-6">
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">McConkie, G. W., & Rayner, K. (1975). The saccadic target and the span of the effective stimulus in fixational reading. Vision Research, 15(6), 731–739.</p>
                <a href="https://doi.org/10.1016/0042-6989(75)90205-0" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" />
                  doi.org/10.1016/0042-6989(75)90205-0
                </a>
              </li>
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">O'Regan, J. K. (1990). Eye movements and reading. In E. Kowler (Ed.), Eye Movements and Their Role in Visual and Cognitive Processes (pp. 395–453). Elsevier.</p>
              </li>
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">Rubin, G. S., & Turano, K. (1992). Reading without saccadic eye movements. Vision Research, 32(5), 895–902.</p>
                <a href="https://doi.org/10.1016/0042-6989(92)90032-e" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" />
                  doi.org/10.1016/0042-6989(92)90032-e
                </a>
              </li>
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">Yu, D., Cheung, S. H., Legge, G. E., & Chung, S. T. (2010). Reading speed in the peripheral visual field of older adults. Vision Research, 50(9), 860–869.</p>
                <a href="https://doi.org/10.1016/j.visres.2010.02.006" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" />
                  doi.org/10.1016/j.visres.2010.02.006
                </a>
              </li>
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">Gannon, H., Bier, L., & Schmidt, K. (2016). Reading on a Smartwatch. HFES Annual Meeting, 60(1), 1223–1227.</p>
                <a href="https://doi.org/10.1177/1541931213601283" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" />
                  doi.org/10.1177/1541931213601283
                </a>
              </li>
              <li className="text-sm text-slate-600 dark:text-slate-400">
                <p className="italic">Benedetto, S., et al. (2015). Rapid serial visual presentation in reading: The case of Spritz. Computers in Human Behavior, 45, 352–358.</p>
                <a href="https://doi.org/10.1016/j.chb.2014.12.043" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline mt-1">
                  <ExternalLink className="w-3 h-3" />
                  doi.org/10.1016/j.chb.2014.12.043
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">
            Experience the Science Yourself
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
            Try RSVP reading free — no signup required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/stories')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Start Reading Free
            </button>
            <button
              onClick={() => navigate('/why')}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-lg font-semibold text-purple-600 bg-purple-50 dark:bg-purple-900/20 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-all"
            >
              Learn How RSVP Works →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchPage;
